import {BaseView} from "@nolimitcity/slot-game/bin/core/base/BaseView";
import {ParticlesManager} from "@nolimitcity/slot-game/bin/core/particles/ParticlesManager";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {ScalableLayer} from "@nolimitcity/slot-game/bin/core/stage/ScalableLayer";
import {IResizeData, StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {AnimatedParticle, Emitter} from "pixi-particles";
import {BookOfShadowsGameConfig} from "../BookOfShadowsGameConfig";
import {BookOfShadowsGameMode} from "../gameMode/BookOfShadowsGameMode";
import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {ServerEvent} from "@nolimitcity/slot-game/bin/core/server/event/ServerEvent";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {BookOfShadowsParsedInitData} from "../server/data/BookOfShadowsParsedInitData";

export class FireParticles extends BaseView {
    private _backgroundConfig0: any = { // fast particles
        "alpha": {
            "start": 1,
            "end": 0.22
        },
        "scale": {
            "start": 0.6,
            "end": 0.8,
            "minimumScaleMultiplier": 0.5
        },
        "color": {
            "start": "#ffffff",
            "end": "#ffffff"
        },
        "speed": {
            "start": 400,
            "end": 200,
            "minimumSpeedMultiplier": 2
        },
        "acceleration": {
            "x": 0,
            "y": 0
        },
        "maxSpeed": 0,
        "startRotation": {
            "min": -15,
            "max": 15
        },
        "noRotation": false,
        "rotationSpeed": {
            "min": 0,
            "max": 15
        },
        "lifetime": {
            "min": 6,
            "max": 8
        },
        "blendMode": "add",
        "frequency": 0.15,
        "emitterLifetime": -1,
        "maxParticles": 200,
        "pos": {
            "x": 0,
            "y": 0
        },
        "addAtBack": false,
        "spawnType": "rect",
        "spawnRect": {
            "x": -1,
            "y": 0,
            "w": 1,
            "h": 720
        }
    };

    private _backgroundConfig1: any = { //middle
        "alpha": {
            "start": 1,
            "end": 0.22
        },
        "scale": {
            "start": 0.4,
            "end": 0.7,
            "minimumScaleMultiplier": 0.5
        },
        "color": {
            "start": "#ffffff",
            "end": "#ffffff"
        },
        "speed": {
            "start": 200,
            "end": 100,
            "minimumSpeedMultiplier": 2
        },
        "acceleration": {
            "x": 0,
            "y": 0
        },
        "maxSpeed": 0,
        "startRotation": {
            "min": -15,
            "max": 15
        },
        "noRotation": false,
        "rotationSpeed": {
            "min": 0,
            "max": 10
        },
        "lifetime": {
            "min": 6,
            "max": 10
        },
        "blendMode": "add",
        "frequency": 0.12,
        "emitterLifetime": -1,
        "maxParticles": 200,
        "pos": {
            "x": 0,
            "y": 0
        },
        "addAtBack": false,
        "spawnType": "rect",
        "spawnRect": {
            "x": -1,
            "y": 0,
            "w": 1,
            "h": 720
        }
    };

    private _backgroundConfig2: any = { //slow
        "alpha": {
            "start": 1,
            "end": 0.22
        },
        "scale": {
            "start": 0.25,
            "end": 0.5,
            "minimumScaleMultiplier": 0.5
        },
        "color": {
            "start": "#ffffff",
            "end": "#ffffff"
        },
        "speed": {
            "start": 100,
            "end": 50,
            "minimumSpeedMultiplier": 2
        },
        "acceleration": {
            "x": 0,
            "y": 0
        },
        "maxSpeed": 0,
        "startRotation": {
            "min": -30,
            "max": 30
        },
        "noRotation": false,
        "rotationSpeed": {
            "min": 0,
            "max": 5
        },
        "lifetime": {
            "min": 10,
            "max": 14
        },
        "blendMode": "add",
        "frequency": 0.1,
        "emitterLifetime": -1,
        "maxParticles": 200,
        "pos": {
            "x": 0,
            "y": 0
        },
        "addAtBack": false,
        "spawnType": "rect",
        "spawnRect": {
            "x": -1,
            "y": 0,
            "w": 1,
            "h": 720
        }
    };

    private _backgroundEmitters: Emitter[] = [];
    private _isPlaying: boolean = false;

    private _backgroundLayer: ScalableLayer;

    private _currentOrientation: string = "";
    private _mode: string = "";
    private _nextMode: string = "";

    /**
     * @description To play or stop the emitters.
     * @param play
     */
    public togglePlay(play: boolean): void {
        if (play === this._isPlaying) {
            return;
        }

        this._isPlaying = play;
        if (play && this._isResizeDirty) {
            this.onResize(this._resizeData);
        }
        this._backgroundEmitters.forEach((emitter: Emitter) => {
            emitter.emit = play;
        });
    }

    /**
     * @inheritDoc
     */
    protected initAnimations(): void {
        super.initAnimations && super.initAnimations();

        this._backgroundEmitters = [];
        this._backgroundLayer = StageManager.getLayer(BookOfShadowsGameConfig.instance.LAYERS.BACKGROUND.name);

        this._isPlaying = true;
    }

    /**
     * @inheritDoc
     * @override To re-add the emitters in the layer on resize.
     * @param resizeData
     */
    protected onResize(resizeData: IResizeData): void {
        super.onResize && super.onResize(resizeData);

        if (this._currentOrientation === resizeData.orientation) {
            return;
        }

        if (!this._isPlaying) {
            this._isResizeDirty = true;
            return;
        }

        this._isResizeDirty = false;
        this._currentOrientation = resizeData.orientation;

        while (this._backgroundEmitters.length > 0) {
            ParticlesManager.removeEmitter(this._backgroundEmitters.pop()!);
        }
        this.addEmitters();

        if (this._mode !== "") {
            this._isPlaying = false;
            this.onGameReady();
        }
    }

    /**
     * @inheritDoc
     */
    protected addEventListeners() {
        super.addEventListeners && super.addEventListeners();

        EventHandler.addEventListener(this, ServerEvent.INIT_DATA_PARSED, (event: GameEvent) => this.onInitDataParsed(event.params[0]));
    }

    /**
     * @description Handler for init data parsed.
     * @param data
     */
    private onInitDataParsed(data: BookOfShadowsParsedInitData): void {
        this._mode = data.mode;
        this._nextMode = data.nextMode;

        this.onGameReady();

        EventHandler.removeEventListener(this, ServerEvent.INIT_DATA_PARSED);
    }

    /**
     * @description To add all the emitters in the layer.
     */
    private addEmitters(): void {
        this._backgroundEmitters.push(this.createEmitter(this._backgroundLayer, [
            GameResources.getTextures("fireParticle1")[0],
            GameResources.getTextures("fireParticle2")[0]
        ], this._backgroundConfig0, false));

        this._backgroundEmitters.push(this.createEmitter(this._backgroundLayer, [
            GameResources.getTextures("fireParticle2")[0],
            GameResources.getTextures("fireParticle3")[0]
        ], this._backgroundConfig1, false));

        this._backgroundEmitters.push(this.createEmitter(this._backgroundLayer, [
            GameResources.getTextures("fireParticle2")[0],
            GameResources.getTextures("fireParticle3")[0]
        ], this._backgroundConfig2, false));
    }

    /**
     * @description To create the emitters.
     * @param container
     * @param particleImages
     * @param config
     * @param isAnimated
     */
    private createEmitter(container: PIXI.Container, particleImages: any, config: any, isAnimated: boolean): Emitter {
        this.updateEmitterRect(config);

        const emitter: Emitter = new Emitter(container, particleImages, config);
        if (isAnimated) {
            emitter.particleConstructor = AnimatedParticle;
        }

        ParticlesManager.addEmitter(emitter);

        return emitter;
    }

    /**
     * @description To update the emitter rectangle during resizing.
     * @param config
     */
    private updateEmitterRect(config: { spawnRect: { x: number, y: number, w: number, h: number } }): void {
        config.spawnRect.x = NolimitApplication.screenBounds.left;
        config.spawnRect.y = NolimitApplication.screenBounds.top;
        config.spawnRect.h = NolimitApplication.screenBounds.height;
    }

    /**
     * @description Called from init animations and init data parsed.
     */
    private onGameReady(): void {
        if (this._mode === BookOfShadowsGameMode.FREESPIN && this._nextMode === BookOfShadowsGameMode.FREESPIN && this._backgroundEmitters.length > 0) {
            this.togglePlay(true);
        } else {
            this._isPlaying = true;
            this.togglePlay(false);
        }
    }
}